import type { BlastSource } from '../../Model/Project/BlastSource.js'

/**
 * Server-affecting SPIN knobs for a source. When present on a source's `shared` state, the cube is
 * sourced from the `spin.*` schema (backend `/api/vis/spin/{scopeId}/cube`) instead of the plain
 * OTU cube (`/api/vis/krona/...`). Both wire the same Arrow shape (the SPIN cube just adds a
 * `spinId` column, which the decoder ignores), so everything downstream is identical.
 *
 * Unlike the FE-only knobs (`selectedPath`, `gradeThreshold`, …), these two change the bytes the
 * server returns, so they participate in the cube identity — see [cubeVariantSuffix].
 */
export interface SpinSelection {
  /** Which clustering algorithm's SPINs to read (e.g. 'SPCFY', 'VSEARCH'). */
  algorithmType: string
  /**
   * ISO-8601 local date-time (`YYYY-MM-DDTHH:mm[:ss]`, no zone) selecting the "latest valid"
   * SPIN assignment per OTU as of that instant. Empty string ⇒ "latest" (the backend defaults
   * `atTime` to now()).
   */
  atTime: string
}

/**
 * Suffix that distinguishes a SPIN cube from the plain OTU cube in every cube key (slice entry,
 * resident-table registry, IndexedDB cache). Empty when there's no SPIN selection, so plain-cube
 * keys stay exactly `${scopeId}:${blastType}` — existing entries and callers are unaffected.
 */
export const cubeVariantSuffix = (spin?: SpinSelection): string =>
  spin ? `:spin:${spin.algorithmType}:${spin.atTime}` : ''

/** The single source of truth for a cube's identity string across slice / registry / cache. */
export const cubeKey = (scopeId: string, blastType: BlastSource, spin?: SpinSelection): string =>
  `${scopeId}:${blastType}${cubeVariantSuffix(spin)}`
